import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  Table,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
  Spinner,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import { BASE_URL } from "../../Globalapi/Globalapi";

const Print_pdf_Exam_result = forwardRef((props, ref) => {
  const [loading, setLoading] = useState(false);
  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedClass, setSelectedClass] = useState(
    localStorage.getItem("selectedClass") || ""
  );
  const [selectedDivision, setSelectedDivision] = useState(
    localStorage.getItem("selectedDivision") || ""
  );
  const [error, setError] = useState(null);
  const printRef = useRef();
  const schoolName = sessionStorage.getItem("school_name");
  const schoolEmail = sessionStorage.getItem("school_email");
  const schoolAddress = sessionStorage.getItem("school_address");
  const schoolImage = sessionStorage.getItem("school_image_url");
  useImperativeHandle(ref, () => ({
    handlePrint,
  }));

  const fetchData = async (classId, divisionId) => {
    try {
      const jwtToken = sessionStorage.getItem("jwt");
      if (!jwtToken) {
        console.error("JWT token not found");
        throw new Error("JWT token not found");
      }

      setLoading(true);

      const [examsRes, categoriesRes] = await Promise.all([
        axios.post(`${BASE_URL}/admin/exam/list_all_exam_details.php`, {
          class_id: selectedClass,
          division_id: selectedDivision,
        }),
        axios.get(`${BASE_URL}/admin/exam/exam_category.php`),
      ]);

      setExams(examsRes.data.data || []);
      setCategories(categoriesRes.data.data || []);
    } catch (error) {
      console.error("Error fetching exam data:", error);
      setError("Error fetching exam data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClass && selectedDivision) {
      fetchData(selectedClass, selectedDivision);
    }
  }, [selectedClass, selectedDivision]);

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`<div>${content}</div>`);
    iframeDoc.close();

    const images = iframeDoc.getElementsByTagName("img");
    const loadPromises = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        })
    );

    Promise.all(loadPromises).then(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      document.body.removeChild(iframe);
    });
  };

  // Organize data for each category and get unique subjects per category
  const getStudentDataByCategory = (category) => {
    // Filter out exams that are class tests
    const examsForCategory = exams.filter(
      (exam) => exam.category === category && exam.is_class_test !== "1"
    );

    // Check if there are any valid marks in this category
    const hasValidMarks = examsForCategory.some((exam) => exam.marks);

    if (!hasValidMarks) {
      return { studentData: {}, allSubjects: [] }; // Return empty data for categories with no marks
    }

    const studentData = examsForCategory.reduce((acc, exam) => {
      if (!acc[exam.studentname]) acc[exam.studentname] = {};
      acc[exam.studentname][exam.subject] = exam.marks || "-"; // Assign marks if available or "-" if not
      return acc;
    }, {});

    const allSubjects = Array.from(
      new Set(examsForCategory.map((exam) => exam.subject))
    );

    return { studentData, allSubjects };
  };

  return (
    <div
      ref={printRef}
      style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "40px",
          borderBottom: "1px solid #e5e5e5",
          paddingBottom: "20px",
          gap: "24px",
        }}
      >
        <img
          src={schoolImage ? `${schoolImage}` : ""}
          alt={`school Logo`}
          style={{
            width: "70px",
            height: "70px",
            objectFit: "contain",
            backgroundColor: "#fff",
            padding: "10px",
          }}
        />

        <div>
          <h1
            style={{
              margin: "0",
              fontSize: "20px",
              fontWeight: "600",
              color: "#1a1a1a",
              fontFamily: "'Times New Roman', serif",
              letterSpacing: "0.5px",
            }}
          >
            {schoolName}
          </h1>
          <h2
            style={{
              margin: "6px 0 0",
              fontSize: "18px",
              fontWeight: "500",
              color: "#666",
              fontFamily: "'Times New Roman', serif",
            }}
          >
            {schoolAddress}
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: "#6C757D",
              margin: "6px 0 0",
              fontFamily: "Arial, sans-serif",
            }}
          >
            {schoolEmail}
          </p>
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <h3>Exam Result</h3>
      </div>

      {loading ? (
        <Spinner size="xl" />
      ) : error ? (
        <Text color="red.500">{error}</Text>
      ) : (
        categories.map((category) => {
          const { studentData, allSubjects } = getStudentDataByCategory(
            category.category
          );

          // Skip rendering if the category has no subjects (indicating no valid marks)
          if (allSubjects.length === 0) return null;

          return (
            <div key={category.id} style={{ marginBottom: "40px" }}>
              <h3 style={{ marginBottom: "20px" }}>{category.category}</h3>
              <Table
                sx={{
                  "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
                }}
                style={{ width: "100%", borderCollapse: "collapse" }}
              >
                <Thead>
                  <Tr>
                    <Th
                      style={{
                        border: "1px solid #d4c2c2",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      Student Name
                    </Th>
                    {allSubjects.map((subject) => (
                      <Th
                        key={subject}
                        style={{
                          border: "1px solid #d4c2c2",
                          padding: "8px",
                          textAlign: "center",
                        }}
                      >
                        {subject}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {Object.entries(studentData).map(
                    ([studentName, subjects]) => (
                      <Tr key={studentName}>
                        <Td
                          style={{
                            border: "1px solid #d4c2c2",
                            padding: "8px",
                            textAlign: "center",
                          }}
                        >
                          {studentName}
                        </Td>
                        {allSubjects.map((subject) => (
                          <Td
                            key={subject}
                            style={{
                              border: "1px solid #d4c2c2",
                              padding: "8px",
                              textAlign: "center",
                            }}
                          >
                            {subjects[subject] || "-"}
                          </Td>
                        ))}
                      </Tr>
                    )
                  )}
                </Tbody>
              </Table>
            </div>
          );
        })
      )}
    </div>
  );
});

export default Print_pdf_Exam_result;
